'use client'
import { createContext, useContext } from 'react';
import { AdmissionChance, AdmissionChanceResult } from "@/types/admission-chance";
import { useSearchParams } from "next/navigation";
import { GET } from '@/api/get-request';
import React from 'react';

interface ApiResponse {
    admission_chance: AdmissionChance;
    result?: AdmissionChanceResult;
}

interface AdmissionChanceContextType {
    apiData: ApiResponse | null;
    setApiData: React.Dispatch<React.SetStateAction<ApiResponse | null>>;
    clearApiData: () => void;
    error: string | null;
    setError: (error: string | null) => void;
    isFinished: boolean | null;
    setIsFinished: (isFinished: boolean | null) => void;
}

const AdmissionChanceContext = createContext<AdmissionChanceContextType | null>(null);

export const AdmissionChanceProvider = ({ children }: { children: React.ReactNode }) => {
    const [apiData, setApiData] = React.useState<ApiResponse | null>(null);
    const [isFinished, setIsFinished] = React.useState<boolean | null>(false);
    const [error, setError] = React.useState<string | null>(null);
    const searchParams = useSearchParams();
    const uuid = searchParams ? searchParams.get('UUID') : null;

    React.useEffect(() => {
        async function fetchAdmissionChance() {
            if (!uuid || apiData?.admission_chance) return;

            try {
                setError(null);
                const response = await GET({ url: `v1/admission-chances/${uuid}` });
                if (response.success) {
                    setApiData(prev => ({
                        ...prev,
                        admission_chance: response.data,
                    }));
                } else {
                    console.error('Error fetching admission chance:', response);
                    setError('Failed to fetch admission chance data');
                }
            } catch (error) {
                console.error('Error:', error);
                setError('An error occurred while fetching data');
            }
        }

        fetchAdmissionChance();
    }, [uuid]);

    const clearApiData = () => setApiData(null);

    return (
        <AdmissionChanceContext.Provider value={{ apiData, setApiData, clearApiData, error, setError, isFinished, setIsFinished }}>
            {children}
        </AdmissionChanceContext.Provider>
    );
};

export const useAdmissionChance = () => {
    const context = useContext(AdmissionChanceContext);
    if (!context) {
        throw new Error('useAdmissionChance must be used within an AdmissionChanceProvider');
    }
    return context;
};